// Third-party packages
import {
	AppDescription, Destination, IAnalytics,
} from '@local_types/index';

// Internal libraries (@stonal-tech/*)
import {
	logger,
} from '@config/logger';

// Vite injects BUILD_VERSION at build time

// @ts-ignore
declare const BUILD_VERSION: string;

/**
 * Get the app version from the page's meta tag
 */
function getAppVersion(): string | undefined {
	const metaVersion = document.querySelector('meta[name="version"]');
	return metaVersion?.getAttribute('content') || undefined;
}

/**
 * Core analytics object
 *
 * This is the main object that allows to track events and identify users
 *
 */
export class Analytics implements IAnalytics {
	private destinations: Destination[] = [];
	private app: AppDescription | undefined = undefined;
	private appVersion: string | undefined = undefined;

	constructor() {
		this.appVersion = getAppVersion();
	}

	setApp(app: AppDescription | undefined) {
		if (app === undefined) {
			return;
		}

		this.app = app;
		this.appVersion = getAppVersion();

		const appProps = this._getAppProperties();
		window.stonal_app = appProps.app;
		window.stonal_env = appProps.stonal_env;
		window.stonal_stack = appProps.stonal_stack;
	}

	page (name?: string, properties?: Record<string, any>) {
		properties = this._extendProperties(properties || {});
		for (let i = 0; i < this.destinations.length; i++) {
			const dest = this.destinations[i];
			dest.page && dest.page(name || '', properties || {});
		}
	}

	identify (userId: string | undefined, traits: Record<string, any>) {
		for (let i = 0; i < this.destinations.length; i++) {
			const dest = this.destinations[i];
			dest.identify && dest.identify(userId, traits);
		}
	}


	group (groupId: string, traits?: Record<string, any>) {
		// Associates the current user with a group/organization
		// Useful for B2B tracking and organization-level analysis
		for (let i = 0; i < this.destinations.length; i++) {
			const dest = this.destinations[i];
			dest.group && dest.group(groupId, traits || {});
		}
	}

	track (event: string, properties?: Record<string, any>) {
		// We always use the stonalEnv property to track the environment
		properties = this._extendProperties(properties);
		for (let i = 0; i < this.destinations.length; i++) {
			const dest = this.destinations[i];
			dest.track && dest.track(event, properties);
		}
	}

	addDestination (dest: Destination) {
		// We check if the destination is already present
		for (let i = 0; i < this.destinations.length; i++) {
			if (this.destinations[i].name === dest.name) {
				logger.error('Destination ' + dest.name + ' already present');
				return;
			}
		}
		this.destinations.push(dest);
	}

	error (event: string, properties: any) {
		logger.error(event + ' : ' + JSON.stringify(properties));
		// Track error event to all destinations with "Error: " prefix
		this.track(`Error: ${event}`, properties);
	}


	private _getAppProperties(): { app: string;
		stonal_env: string | undefined;
		stonal_stack: string | undefined } {
		// This method should only be called when this.app is defined
		// Use tracking_code if available, otherwise fall back to code
		return {
			app: this.app!.tracking_code || this.app!.code,
			stonal_env: this.app!.env,
			stonal_stack: this.app!.stack,
		};
	}

	private _extendProperties (properties: Record<string, any> | undefined): Record<string, any> {
		properties = properties || {};
		if (this.app) {
			const appProps = this._getAppProperties();
			properties.app = appProps.app;
			properties.stonal_env = appProps.stonal_env;
			properties.stonal_stack = appProps.stonal_stack;
		}
		if (this.appVersion) {
			properties.app_version = this.appVersion;
		}
		// Inject the build version from the environment
		properties.stonal_tracking_version = typeof BUILD_VERSION !== 'undefined' ? BUILD_VERSION : 'dev';
		return properties;
	}
}
